import moment from 'moment'
import React, { useEffect, useState } from 'react'

import { EuiButtonIcon, EuiFlexGroup, EuiFlexItem, EuiHorizontalRule, EuiPanel, EuiSpacer, EuiText, EuiTitle } from '@elastic/eui'

import { PostalCodeTarget } from 'api/interfaces'
import { MasterCampaignPlanCampaignBuilderType, MasterCampaignPlanCampaignBuilder } from 'api/interfaces/MasterCampaignStub'
import AgencyV2ModalOld from 'features/planner/old-pages/components/AgencyV2ModalOld'
import { getIntervalUnitLabel } from 'utils/MasterCampaignPlanUtils'

interface CampaignCardProps {
  builder: MasterCampaignPlanCampaignBuilder
  inEditMode: boolean
  onEditClick: () => void
  onRemoveCampaignClick: () => Promise<void>
  onRemoveCampaignLoading: boolean
}

export const CampaignCardOld = ({ builder, inEditMode, onEditClick, onRemoveCampaignLoading, ...props }: CampaignCardProps) => {
  const [isRemoveModalVisible, setIsRemoveModalVisible] = useState(false)
  const [groupedPostalCodes, setGroupedPostalCodes] = useState<{ city: string; postalCodes: PostalCodeTarget[] }[]>([])

  useEffect(() => {
    const grouped: { city: string; postalCodes: PostalCodeTarget[] }[] = []

    builder.zips?.forEach(target => {
      if (!target.name?.includes(',')) {
        return
      }

      const [, city] = target.name.split(',')
      let postalCodeGroup = grouped.find(g => g.city === city)
      if (!grouped.find(g => g.city === city)) {
        postalCodeGroup = { city, postalCodes: [] }
        grouped.push(postalCodeGroup)
      }
      postalCodeGroup?.postalCodes.push(target)
    })

    setGroupedPostalCodes(grouped)
  }, [builder.zips])

  const onRemoveCampaignClick = () => {
    setIsRemoveModalVisible(true)
  }

  const onConfirmRemoveCampaignClick = async () => {
    await props.onRemoveCampaignClick()

    setIsRemoveModalVisible(false)
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })

  const typeColors: Partial<Record<MasterCampaignPlanCampaignBuilderType, string>> = {
    Internet: '#6B7F5B',
    TV: '#8B5A3E',
    Billboard: '#C49A4D'
  }

  return (
    <>
      <EuiFlexGroup>
        <EuiFlexItem>
          <EuiPanel hasShadow={true} paddingSize={'m'} style={{ backgroundColor: '#ede7dd' }}>
            {inEditMode && (
              <>
                <EuiFlexGroup justifyContent='flexEnd'>
                  <EuiFlexItem grow={false}>
                    <EuiButtonIcon iconType={'pencil'} onClick={onEditClick} />
                  </EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    <EuiButtonIcon iconType={'trash'} onClick={onRemoveCampaignClick} />
                  </EuiFlexItem>
                </EuiFlexGroup>
                <EuiSpacer size={'l'} />
              </>
            )}
            <EuiFlexGroup>
              <EuiFlexItem>
                <EuiTitle size={'s'}>
                  <p>Campaign: {builder.name}</p>
                </EuiTitle>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <div
                  key={builder.id}
                  style={{
                    width: 75,
                    height: 25,
                    background: typeColors[builder.campaignType],
                    borderRadius: 2,
                    color: 'white',
                    alignItems: 'center',
                    justifyContent: 'center',
                    display: 'flex'
                  }}>
                  <span>{builder.campaignType}</span>
                </div>
              </EuiFlexItem>
            </EuiFlexGroup>
            <EuiFlexGroup>
              <EuiFlexItem>
                <span>
                  <b>Starts:</b> {moment(builder.startDate).format('MMMM Do YYYY')}
                  <br />
                  <b>Duration:</b> {builder.intervalDuration} {getIntervalUnitLabel(builder.intervalUnit, builder.intervalDuration ?? 1)} {builder.intervalFrequency && builder.intervalFrequency > 1 ? `(every ${builder.intervalFrequency} ${getIntervalUnitLabel(builder.intervalUnit, builder.intervalFrequency)})` : ''}
                </span>
              </EuiFlexItem>
            </EuiFlexGroup>
            <EuiFlexGroup>
              <EuiFlexItem>
                <span>
                  <b>Budget:</b> {formatter.format(builder.intervalBudget)}
                  {builder.intervalFrequency && builder.intervalFrequency > 1 ? `/every ${builder.intervalFrequency} ${getIntervalUnitLabel(builder.intervalUnit, builder.intervalFrequency)}` : ''}
                  {builder.intervalFrequency && builder.intervalFrequency === 1 ? `/${getIntervalUnitLabel(builder.intervalUnit, builder.intervalFrequency)}` : ''}
                </span>
              </EuiFlexItem>
            </EuiFlexGroup>
            <EuiFlexGroup>
              <EuiFlexItem>{builder.shortSummary}</EuiFlexItem>
            </EuiFlexGroup>

            <EuiFlexGroup>
              <EuiFlexItem grow={4}>
                {groupedPostalCodes.length > 0 && (
                  <>
                    <EuiSpacer size={'l'} />
                    <b>Assigned Geo-Targeting</b>
                    <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                    <ul>
                      {groupedPostalCodes.map(({ city, postalCodes }, index) => (
                        <li key={index}>
                          <b>{city}</b>
                          <EuiSpacer size='xs' />
                          {postalCodes.map((postalCode, index) => (
                            <ul key={index}>
                              <li key={index}>{postalCode.code}</li>
                            </ul>
                          ))}
                          <EuiSpacer size='s' />
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </EuiFlexItem>
              <EuiFlexItem grow={6}>
                {(builder.segmentTargets?.length ?? 0) > 0 && (
                  <>
                    <EuiSpacer size={'l'} />

                    <b>Applied Audience Segments</b>

                    <EuiHorizontalRule size='full' style={{ backgroundColor: 'black' }} margin='xs' />
                  </>
                )}

                {builder.segmentTargets && (
                  <ul>
                    {builder.segmentTargets.map((segment, index) => (
                      <li key={index}>
                        <b>{segment.hierarchy[segment.hierarchy.length - 1]}</b>
                        <EuiSpacer size='xs' />
                        {segment.name}
                        <EuiSpacer size='s' />
                      </li>
                    ))}
                  </ul>
                )}
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiPanel>
        </EuiFlexItem>
      </EuiFlexGroup>
      {isRemoveModalVisible && (
        <AgencyV2ModalOld isLoading={onRemoveCampaignLoading} title='Remove Campaign' onClose={() => setIsRemoveModalVisible(false)} confirmButtonText='Remove' onConfirmButtonClick={onConfirmRemoveCampaignClick} showCancelButton>
          <EuiText>
            Campaign: <strong>{builder.name}</strong> will be removed from the plan.
          </EuiText>
        </AgencyV2ModalOld>
      )}
    </>
  )
}
